/**
 * SC-05 — Create a new slide with the response (AIVA-6799).
 *
 * Adds one slide immediately after the selected slide (gap G-5), puts the reply
 * on it — line 1 as the title (28pt bold), the remaining lines as the body
 * (18pt) — and selects it. Nothing else in the deck is touched, so a single
 * Ctrl+Z removes it (spec §8: SC-05 is additive and writes without confirming).
 *
 * Deliberately not Deckorate's `addContentSlide` (create-slide/hooks/slideInsert.ts),
 * which looks similar but misses three acceptance criteria: it always uses the
 * deck's FIRST master (AC-3 asks for the selected slide's, so a custom template
 * keeps its look), always appends at the END (AC-4 asks for the position after
 * the selection), and writes literal "• " characters into fixed 16:9 boxes.
 *
 * How the spec's sketch maps onto Office.js:
 *  - `slides.add()` takes no position (the spec's `slideIndex` option does not
 *    exist); it appends. The slide is then moved with `moveTo` (PowerPointApi
 *    1.8). A host without `moveTo` keeps the slide at the end rather than failing.
 *  - "Inheriting the active master" is `slideMasterId` = the selected slide's
 *    master. The layout is that master's Blank layout: the spec draws the title
 *    and body as text boxes, and a content layout would leave its own empty
 *    "Click to add text" placeholders behind them (AC-2: no empty body box).
 *    With no Blank layout (or one the host rejects), the default layout is used
 *    and its empty placeholders are deleted, which comes to the same slide.
 *  - Selecting it is `setSelectedSlides` (PowerPointApi 1.5), best-effort.
 */

import {
  NO_SLIDE_SELECTED_ERROR,
  NOTHING_TO_INSERT_ERROR,
  POWERPOINT_UNAVAILABLE_ERROR,
  isBulletList,
  readSlideSize,
  toSlideText,
  type SlideSize,
  type TextBoxGeometry,
} from './insertIntoSlide';

/** Thrown when the host cannot add slides through the API at all. */
export const ADD_SLIDE_UNAVAILABLE_ERROR = 'Adding slides is unavailable on this PowerPoint host';

/** Title size on the new slide (spec SC-05 AC-1). */
export const NEW_SLIDE_TITLE_FONT_SIZE = 28;
/** Body size on the new slide (spec SC-05 AC-1). */
export const NEW_SLIDE_BODY_FONT_SIZE = 18;

export interface NewSlideText {
  readonly title: string;
  /** Empty for a one-line reply: AC-2 wants no body box at all then. */
  readonly body: string;
}

/**
 * Split a reply into the slide's title and body (spec SC-05 behaviour).
 *
 * The reply is cleaned for a slide first ({@link toSlideText}: no Markdown, no
 * bullet markers). The first non-blank line is the title; everything after it
 * is the body, with blank lines dropped — each body line becomes one paragraph.
 */
export function splitTitleAndBody(text: string): NewSlideText {
  const lines = toSlideText(text)
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  return { title: lines[0] ?? '', body: lines.slice(1).join('\n') };
}

export interface NewSlideGeometry {
  readonly title: TextBoxGeometry;
  readonly body: TextBoxGeometry;
}

/**
 * Where the title and body go, as shares of the real slide size (the same
 * proportional approach as SC-02's gap G-4, so 4:3 and custom stages place
 * correctly). On the 960×540 stage the boxes are the familiar title band at the
 * top (50, 30, 860×80) and the body below it (50, 130, 860×370), ending inside
 * the slide. Both auto-fit, so long text shrinks rather than overflowing.
 */
export function newSlideGeometry(size: SlideSize): NewSlideGeometry {
  const left = size.width * (50 / 960);
  const width = size.width * (860 / 960);
  return {
    title: { left, top: size.height * (30 / 540), width, height: size.height * (80 / 540) },
    body: { left, top: size.height * (130 / 540), width, height: size.height * (370 / 540) },
  };
}

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

/**
 * The options for `slides.add`: the selected slide's master and its Blank
 * layout. Undefined when the master cannot be read — `slides.add()` with no
 * options then uses the previous slide's master, which is still the deck's own.
 */
async function resolveAddOptions(
  context: PowerPoint.RequestContext,
  selected: PowerPoint.Slide
): Promise<PowerPoint.AddSlideOptions | undefined> {
  try {
    const master = selected.slideMaster;
    master.load('id');
    const layouts = master.layouts;
    layouts.load('items/id,items/name');
    await context.sync();
    const blank = layouts.items.find((l) => /blank/i.test(l.name));
    return blank ? { slideMasterId: master.id, layoutId: blank.id } : { slideMasterId: master.id };
  } catch {
    return undefined; // master/layouts not readable on this host
  }
}

/**
 * Add the slide and return its id. A rejected master or layout is retried with
 * the host default, so the user still gets a slide; a host with no `slides.add`
 * at all throws {@link ADD_SLIDE_UNAVAILABLE_ERROR}.
 */
async function addSlide(
  context: PowerPoint.RequestContext,
  options: PowerPoint.AddSlideOptions | undefined
): Promise<string> {
  const slides = context.presentation.slides;
  if (typeof (slides as unknown as { add?: unknown }).add !== 'function') {
    throw new TypeError(ADD_SLIDE_UNAVAILABLE_ERROR);
  }
  slides.load('items/id');
  await context.sync();
  const before = new Set(slides.items.map((s) => s.id));

  try {
    slides.add(options);
    await context.sync();
  } catch (error) {
    if (!options) throw error;
    slides.add();
    await context.sync();
  }

  slides.load('items/id');
  await context.sync();
  const added = slides.items.find((s) => !before.has(s.id));
  if (!added) throw new Error(ADD_SLIDE_UNAVAILABLE_ERROR);
  return added.id;
}

/**
 * Move the new slide to just after the selected one (G-5). `moveTo` needs
 * PowerPointApi 1.8; without it the slide stays at the end of the deck, which
 * is where `slides.add` put it — still added, just not repositioned.
 */
async function moveAfter(
  context: PowerPoint.RequestContext,
  newSlideId: string,
  afterSlideId: string
): Promise<void> {
  try {
    const slides = context.presentation.slides;
    slides.load('items/id');
    await context.sync();
    const ids = slides.items.map((s) => s.id);
    const target = ids.indexOf(afterSlideId) + 1;
    const current = ids.indexOf(newSlideId);
    if (target <= 0 || current === target) return;
    slides.getItem(newSlideId).moveTo(target);
    await context.sync();
  } catch {
    /* moveTo unavailable on this host */
  }
}

/**
 * Delete the placeholders the layout put on the new slide that are still empty
 * (none on a Blank layout). A placeholder that already shows something — a date
 * or slide-number field — is kept.
 */
async function removeEmptyPlaceholders(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide
): Promise<void> {
  try {
    const shapes = slide.shapes;
    shapes.load('items/type');
    await context.sync();
    const placeholders = shapes.items.filter((s) => String(s.type).toLowerCase() === 'placeholder');
    if (placeholders.length === 0) return;
    for (const s of placeholders) s.textFrame.load('hasText');
    await context.sync();
    for (const s of placeholders) if (!s.textFrame.hasText) s.delete();
    await context.sync();
  } catch {
    /* shapes unreadable — the text boxes still go on; at worst a prompt shows */
  }
}

/** Best-effort formatting, as SC-02: a host that refuses it still gets the text. */
function formatBox(
  shape: PowerPoint.Shape,
  size: number,
  options: { bold?: boolean; bullets?: boolean }
): void {
  try {
    shape.textFrame.wordWrap = true;
    shape.textFrame.autoSizeSetting = 'AutoSizeTextToFitShape';
  } catch {
    /* autosize unavailable on this host */
  }
  try {
    shape.textFrame.textRange.font.size = size;
    if (options.bold) shape.textFrame.textRange.font.bold = true;
  } catch {
    /* font API unavailable on this host */
  }
  if (options.bullets) {
    try {
      shape.textFrame.textRange.paragraphFormat.bulletFormat.visible = true;
    } catch {
      /* bullet API unavailable — the lines still go in, unbulleted */
    }
  }
}

export interface AddAsNewSlideResult {
  readonly added: true;
  readonly slideId: string;
}

/**
 * Add the reply as a new slide after the selected one and select it.
 *
 * Throws {@link NO_SLIDE_SELECTED_ERROR}, {@link POWERPOINT_UNAVAILABLE_ERROR},
 * {@link NOTHING_TO_INSERT_ERROR} or {@link ADD_SLIDE_UNAVAILABLE_ERROR}; any
 * other error is a host failure.
 */
export async function addAsNewSlide(text: string): Promise<AddAsNewSlideResult> {
  if (!hasPpt()) throw new Error(POWERPOINT_UNAVAILABLE_ERROR);
  const { title, body } = splitTitleAndBody(text);
  if (!title) throw new Error(NOTHING_TO_INSERT_ERROR);

  return await ppt().run(async (context) => {
    const selection = context.presentation.getSelectedSlides();
    selection.load('items/id');
    await context.sync();
    // Several slides selected: the new one goes after the last, like Deckorate's "current".
    const selected = selection.items[selection.items.length - 1];
    if (!selected) throw new Error(NO_SLIDE_SELECTED_ERROR);

    const options = await resolveAddOptions(context, selected);
    const slideId = await addSlide(context, options);
    await moveAfter(context, slideId, selected.id);

    // Always, not only without a Blank layout: addSlide may have fallen back to
    // the host default after the Blank layout was rejected. On a Blank slide
    // there is nothing to remove.
    const slide = context.presentation.slides.getItem(slideId);
    await removeEmptyPlaceholders(context, slide);

    const geometry = newSlideGeometry(await readSlideSize(context));
    const titleBox = slide.shapes.addTextBox(title, geometry.title);
    formatBox(titleBox, NEW_SLIDE_TITLE_FONT_SIZE, { bold: true });
    if (body) {
      const bodyBox = slide.shapes.addTextBox(body, geometry.body);
      formatBox(bodyBox, NEW_SLIDE_BODY_FONT_SIZE, { bullets: isBulletList(body) });
    }
    await context.sync();

    try {
      context.presentation.setSelectedSlides([slideId]);
      await context.sync();
    } catch {
      /* selection API unavailable (needs PowerPointApi 1.5) — the slide is still added */
    }
    return { added: true as const, slideId };
  });
}
